/**
 * Editor Keyboard Shortcuts Hook
 * Handles Ctrl+C/V/D (copy/paste/duplicate), Ctrl+Z/Y (undo/redo), Delete, Ctrl+S (save)
 */
import React, { useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useEditor } from "@craftjs/core";
import { blockResolver } from "../../../config/blockResolver";

// Get unstable_batchedUpdates from ReactDOM
const batchedUpdates = (ReactDOM as any).unstable_batchedUpdates || ((fn: () => void) => fn());

// Store copied node data globally (persists across component re-renders)
let clipboardData: string | null = null;

export const useEditorShortcuts = () => {
  const { actions, query, enabled, selectedNodeId } = useEditor((state) => ({
    enabled: state.options.enabled,
    selectedNodeId: state.events.selected.size > 0 ? Array.from(state.events.selected)[0] : null,
  }));

  // Undo
  const undo = useCallback(() => {
    try {
      if (!query.history.canUndo()) return false;
      actions.history.undo();
      return true;
    } catch {
      return false;
    }
  }, [actions, query]);

  // Redo
  const redo = useCallback(() => {
    try {
      if (!query.history.canRedo()) return false;
      actions.history.redo();
      return true;
    } catch {
      return false;
    }
  }, [actions, query]);

  // Copy selected node with all children
  const copyNode = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === "ROOT") return false;

    try {
      const nodeInfo = query.node(selectedNodeId);
      const descendants = nodeInfo.descendants(true); // true = deep
      const allNodeIds = [selectedNodeId, ...descendants];

      // Serialize all nodes (root + descendants)
      const nodes: Record<string, any> = {};
      allNodeIds.forEach((nodeId) => {
        const node = query.node(nodeId);
        const serialized = node.toSerializedNode();
        nodes[nodeId] = {
          ...serialized,
          name: node.get().data.name,
          displayName: node.get().data.displayName,
          childNodes: node.get().data.nodes || [],
          linkedNodes: node.get().data.linkedNodes || {},
        };
      });

      clipboardData = JSON.stringify({
        rootNodeId: selectedNodeId,
        nodes,
      });
      return true;
    } catch {
      return false;
    }
  }, [query, selectedNodeId]);

  // Recursively create node tree from clipboard data
  const createNodeTree = useCallback((nodeId: string, data: any, parentId: string | null): any => {
    const nodeData = data.nodes[nodeId];
    if (!nodeData) return null;

    const componentName = nodeData.type?.resolvedName || nodeData.displayName || nodeData.name;
    const Component = (blockResolver as any)[componentName];
    if (!Component) return null;

    // Create element with props
    const element = React.createElement(Component, nodeData.props || {});
    const freshTree = query.parseReactElement(element).toNodeTree();
    const newRootId = freshTree.rootNodeId;

    // If this node has children, we need to add them
    const childNodes = nodeData.childNodes || [];
    if (childNodes.length > 0) {
      childNodes.forEach((childId: string) => {
        const childTree = createNodeTree(childId, data, newRootId);
        if (childTree) {
          // Merge child tree nodes into fresh tree
          Object.assign(freshTree.nodes, childTree.nodes);
          // Add child to parent's nodes array
          const parentNode = freshTree.nodes[newRootId];
          if (parentNode && parentNode.data) {
            if (!parentNode.data.nodes) parentNode.data.nodes = [];
            parentNode.data.nodes.push(childTree.rootNodeId);
          }
        }
      });
    }

    return freshTree;
  }, [query]);

  // Paste copied node with children
  const pasteNode = useCallback(() => {
    if (!clipboardData) return false;

    try {
      const data = JSON.parse(clipboardData);
      const rootSerializedNode = data.nodes[data.rootNodeId];
      if (!rootSerializedNode) return false;

      // Determine target parent
      let targetParent = "ROOT";
      if (selectedNodeId && selectedNodeId !== "ROOT") {
        const selectedNode = query.node(selectedNodeId);
        if (selectedNode.isCanvas()) {
          targetParent = selectedNodeId;
        } else {
          targetParent = selectedNode.get().data.parent || "ROOT";
        }
      }

      // Create the full node tree with children
      const nodeTree = createNodeTree(data.rootNodeId, data, targetParent);
      if (!nodeTree) return false;

      // Add to editor and select
      actions.addNodeTree(nodeTree, targetParent);
      setTimeout(() => actions.selectNode(nodeTree.rootNodeId), 50);

      return true;
    } catch {
      return false;
    }
  }, [query, actions, selectedNodeId, createNodeTree]);

  // Duplicate selected node
  const duplicateNode = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === "ROOT") return false;
    return copyNode() && pasteNode();
  }, [copyNode, pasteNode, selectedNodeId]);

  // Check if a node is a linked node (cannot be deleted)
  const isLinkedNode = useCallback((nodeId: string): boolean => {
    try {
      const parentId = query.node(nodeId).get().data.parent;
      if (!parentId) return false;
      const linkedNodes = query.node(parentId).get().data.linkedNodes || {};
      return Object.values(linkedNodes).includes(nodeId);
    } catch { return false; }
  }, [query]);

  // Delete selected node - wrapped in batchedUpdates for batching
  const deleteNode = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === "ROOT") return false;
    if (isLinkedNode(selectedNodeId)) return false;

    const parentId = query.node(selectedNodeId).get().data.parent;

    batchedUpdates(() => {
      actions.delete(selectedNodeId);
      if (parentId) {
        actions.selectNode(parentId);
      }
    });

    return true;
  }, [actions, query, selectedNodeId, isLinkedNode]);

  // Save
  const triggerSave = useCallback(() => {
    window.dispatchEvent(new CustomEvent("editor-save"));
    return true;
  }, []);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!enabled) return;

      const code = e.code;
      const target = e.target as HTMLElement;
      const isInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

      // Ctrl+S - Save (always works)
      if (e.ctrlKey && code === "KeyS") {
        e.preventDefault();
        triggerSave();
        return;
      }

      // Ctrl+Z - Undo (always works)
      if (e.ctrlKey && code === "KeyZ" && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        undo();
        return;
      }

      // Ctrl+Y or Ctrl+Shift+Z - Redo (always works)
      if ((e.ctrlKey && code === "KeyY" && !e.shiftKey && !e.altKey) ||
          (e.ctrlKey && code === "KeyZ" && e.shiftKey && !e.altKey)) {
        e.preventDefault();
        redo();
        return;
      }

      // Don't handle other shortcuts in input fields
      if (isInput) return;

      // Ctrl+C - Copy
      if (e.ctrlKey && code === "KeyC" && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        copyNode();
        return;
      }

      // Ctrl+V - Paste
      if (e.ctrlKey && code === "KeyV" && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        pasteNode();
        return;
      }

      // Ctrl+D - Duplicate
      if (e.ctrlKey && code === "KeyD" && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        duplicateNode();
        return;
      }

      // Delete or Backspace
      if ((code === "Delete" || code === "Backspace") && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        // Call directly like Settings Panel does
        deleteNode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, copyNode, pasteNode, duplicateNode, deleteNode, triggerSave, undo, redo]);

  return {
    copyNode,
    pasteNode,
    duplicateNode,
    deleteNode,
    triggerSave,
    undo,
    redo,
    canUndo: () => query.history.canUndo(),
    canRedo: () => query.history.canRedo(),
    hasClipboard: !!clipboardData,
  };
};

export default useEditorShortcuts;
